import { ListStyle } from '@yleisradio/areena-types';
import classNames from 'classnames';
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ListLayoutProps } from '..';
import { useCards } from 'hooks/useCards';
import { useListPagination } from 'hooks/useListPagination';
import { useTranslation } from 'hooks/useTranslation';
import { AlternateButton } from 'components/AlternateButton';
import { Container } from 'components/Container';
import { ListNotifications } from 'components/List/ListNotifications/ListNotifications';
import { useListEngagementData } from 'components/SnowplowEngagementAnalytics/hooks';
import { CardPage } from './CardPage';
import styles from './GridList.module.scss';
import { useHideEmptyList } from '../hooks/useHideEmptyList';
import { ListMetadata } from '../ListMetadata';

const PAGE_SIZE = 16;

function getAspectRatioClass(
  listStyle: ListStyle | undefined
): string | undefined {
  if (!listStyle?.image || listStyle.image === '16:9') {
    return styles.listWithWideImages;
  } else {
    return styles.listWithNarrowImages;
  }
}

function getCardImageSizes(listStyle: ListStyle | undefined): string {
  if (!listStyle?.image || listStyle.image === '16:9') {
    return '(min-width: 1020px) 23vw, (min-width: 480px) 30vw, 45vw';
  } else {
    return '(min-width: 1400px) 15vw, (min-width: 800px) 18vw, (min-width: 640px) 23vw, (min-width: 480px) 26vw, 45vw';
  }
}

export const GridList: FunctionComponent<ListLayoutProps> = ({
  hideList,
  index,
  list,
  listKey,
  notificationComponent,
  tabTitle,
}) => {
  const { visiblePages, showMore: incrementVisiblePages } =
    useListPagination(listKey);

  const {
    cards: initialCards,
    errored: hasInitialCardFetchErrored,
    loading: isLoadingFirstCards,
    meta: initialCardsMeta,
    notifications,
    totalCount: totalCardCount,
  } = useCards({
    source: list.source,
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });

  useHideEmptyList({
    cardCount: totalCardCount,
    hasCardFetchErrored: hasInitialCardFetchErrored,
    hideList,
    isListLoadingFirstCards: isLoadingFirstCards,
    notifications,
  });

  const t = useTranslation();
  const cardImageSizes = getCardImageSizes(list.style);
  const groupEngagementData = useListEngagementData({
    cardPageMeta: initialCardsMeta,
    isPaginated: false,
    list,
    listIndex: index,
    tabTitle,
  });

  const [isObserverSupported, setObserverSupport] = useState(true);
  const loadingTriggerElementRef = useRef<HTMLDivElement>(null);

  const visibleCardCount = visiblePages * PAGE_SIZE;
  const areAllCardsVisible = visibleCardCount >= totalCardCount;

  const showMore = useCallback(() => {
    if (!isLoadingFirstCards && !areAllCardsVisible) {
      incrementVisiblePages();
    }
  }, [areAllCardsVisible, incrementVisiblePages, isLoadingFirstCards]);

  useEffect(() => {
    if (isObserverSupported && !('IntersectionObserver' in window)) {
      setObserverSupport(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      !isObserverSupported ||
      !loadingTriggerElementRef.current ||
      isLoadingFirstCards ||
      areAllCardsVisible
    )
      return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) {
        showMore();
      }
    });

    observer.observe(loadingTriggerElementRef.current);

    return () => {
      observer.disconnect();
    };
  }, [areAllCardsVisible, isLoadingFirstCards, isObserverSupported, showMore]);

  return (
    <Container>
      <ListMetadata cards={initialCards} />
      <ListNotifications
        notifications={notifications}
        listStyle={list.style}
        notificationComponent={notificationComponent}
      />
      <ul className={classNames(styles.list, getAspectRatioClass(list.style))}>
        {Array.from({ length: visiblePages }, (_, pageIndex) => {
          return (
            <CardPage
              cardImageSizes={cardImageSizes}
              groupEngagementData={groupEngagementData}
              key={pageIndex}
              listStyle={list.style}
              pageIndex={pageIndex}
              pageKey={`${listKey}-${pageIndex}`}
              pageSize={PAGE_SIZE}
              source={list.source}
              totalCount={totalCardCount}
            />
          );
        })}
      </ul>

      {!isLoadingFirstCards ? <div ref={loadingTriggerElementRef} /> : null}

      {!isObserverSupported && !areAllCardsVisible && (
        <div className={styles.loadMoreButton}>
          <AlternateButton
            text={t('showMore')}
            onClick={showMore}
            size={'large'}
            textAlignment={'center'}
          />
        </div>
      )}
    </Container>
  );
};
